import { Link } from 'react-router-dom';
import SectionEyebrow from '../components/SectionEyebrow';
import ImageSlot from '../components/ImageSlot';
import Reveal from '../components/Reveal';
import { lotTiers } from '../data/lots';

export default function Home() {
  return (
    <div>
      {/* HERO: photo + scrim + SVG art layer */}
      <section className="relative min-h-screen flex flex-col justify-end overflow-hidden grain">
        {/* Background photograph */}
        <div className="absolute inset-0">
          <ImageSlot
            id="home.hero"
            className="absolute inset-0 w-full h-full"
            aspect="auto"
            loading="eager"
            position="center"
          />
        </div>

        {/* Tonal scrim, forest-deep at the bottom */}
        <div className="absolute inset-0 bg-gradient-to-b from-forest-950/35 via-forest-950/10 to-forest-950/85" />

        {/* Subtle SVG botanical accents on top */}
        <svg
          className="absolute inset-0 w-full h-full opacity-15"
          viewBox="0 0 1440 900"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden
        >
          <defs>
            <radialGradient id="glow1" cx="20%" cy="30%" r="50%">
              <stop offset="0%" stopColor="#90b193" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#1f2f24" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="glow2" cx="80%" cy="80%" r="60%">
              <stop offset="0%" stopColor="#d9bc88" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#1f2f24" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect width="1440" height="900" fill="url(#glow1)" />
          <rect width="1440" height="900" fill="url(#glow2)" />
          <path
            d="M-50 600 Q300 580 500 640 T1000 700 T1500 780"
            stroke="#d9bc88"
            strokeWidth="1.5"
            fill="none"
            opacity="0.4"
            strokeDasharray="4 8"
          />
        </svg>

        {/* Top tag */}
        <div className="absolute top-28 left-0 right-0 z-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="inline-flex items-center gap-3 px-4 py-2 border border-cream/25 backdrop-blur-sm bg-forest-950/20">
              <span className="w-1.5 h-1.5 rounded-full bg-sand-300 animate-pulse" />
              <span className="eyebrow text-cream/95">
                Phase 1 . Open now
              </span>
            </div>
          </div>
        </div>

        {/* Headline */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pb-24 lg:pb-32">
          <div className="max-w-5xl">
            <h1 className="font-display text-cream leading-[0.95] tracking-tight text-[clamp(3rem,9vw,8rem)]">
              Live in <span className="italic text-sand-300">harmony.</span>
              <br />
              Naturally.
            </h1>
            <p className="mt-8 max-w-2xl text-cream/90 text-lg lg:text-xl font-light leading-relaxed">
              A 160-hectare regenerative community on Costa Rica&rsquo;s
              Caribbean coast. River to ocean. Eight private neighborhoods.
              The first masterplan-scale sustainable village on this side of
              the country.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/reserve"
                className="px-7 py-4 bg-sand-300 text-forest-900 font-medium tracking-wide hover:bg-sand-200 transition-colors"
              >
                Reserve Your Lot
              </Link>
              <Link
                to="/land"
                className="px-7 py-4 border border-cream/40 text-cream font-medium tracking-wide hover:bg-cream/10 transition-colors backdrop-blur-sm"
              >
                See the Land
              </Link>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="hidden md:flex items-center gap-3 mt-20 text-cream/60 text-xs uppercase tracking-widest-2">
            <span className="w-12 h-px bg-cream/40" />
            Scroll
          </div>
        </div>
      </section>

      {/* QUICK STATS BAR */}
      <section className="bg-forest-900 text-cream py-12 border-t border-cream/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6">
            <Stat top="#1" label="Global destination" sub="2026" />
            <Stat top="160 ha" label="River to sea" sub="clean title" />
            <Stat
              top="20 min"
              label="From Limon airport"
              sub="2.5 hrs from San Jose"
            />
            <Stat top="< 1 km" label="To the beach" sub="concession" />
          </div>
        </div>
      </section>

      {/* WHY NOW: text + image */}
      <section className="py-24 lg:py-32 bg-cream relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <Reveal as="div" className="lg:col-span-5">
              <SectionEyebrow>Why now</SectionEyebrow>
              <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-tight text-forest-900">
                The Caribbean coast is having{' '}
                <span className="italic text-clay-500">its moment.</span>
              </h2>
              <div className="mt-10 aspect-[3/4] overflow-hidden">
                <ImageSlot
                  id="home.why-now"
                  className="w-full h-full"
                  aspect="auto"
                />
              </div>
            </Reveal>
            <Reveal
              as="div"
              delay={100}
              className="lg:col-span-7 lg:pt-16 space-y-6 text-forest-900/80 text-lg leading-relaxed"
            >
              <p>
                Limon Province was just named the{' '}
                <strong className="text-forest-900">
                  #1 global destination of 2026
                </strong>{' '}
                by Skyscanner. Route 36 is freshly paved. International
                buyers are arriving. Land prices are climbing fast, and they
                have a long way to go before catching up to the Pacific
                coast, where comparable land costs{' '}
                <strong className="text-forest-900">2 to 5 times more</strong>.
              </p>
              <p>
                Phase 1 buyers in well-planned Costa Rica communities have
                historically seen lot values rise 2 to 4x by the time later
                phases sell out. Early pricing is the single largest
                advantage in this market.
              </p>
              <p className="font-display italic text-2xl lg:text-3xl text-forest-900 pt-2 leading-snug">
                This is the inflection point. Once the rest of the world
                catches on, the math changes.
              </p>
              <Link
                to="/calculator"
                className="inline-flex items-center gap-2 mt-4 text-clay-600 font-medium hover:text-clay-700 transition-colors"
              >
                Run the numbers
                <span aria-hidden>&rarr;</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* LAND OVERVIEW: full bleed image + stats */}
      <section className="relative bg-forest-900 text-cream overflow-hidden">
        <div className="relative grid lg:grid-cols-2 min-h-[80vh]">
          <div className="relative">
            <ImageSlot
              id="home.land-overview"
              className="absolute inset-0 w-full h-full"
              aspect="auto"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-forest-900/40 lg:bg-gradient-to-r lg:from-forest-900/20 lg:to-forest-900" />
          </div>
          <div className="relative p-10 lg:p-20 flex flex-col justify-center">
            <SectionEyebrow>
              <span className="text-sand-300">The land</span>
            </SectionEyebrow>
            <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-tight">
              A river to the sea.{' '}
              <span className="italic text-sand-300">One vision.</span>
            </h2>
            <p className="mt-6 text-cream/85 text-lg leading-relaxed max-w-xl">
              San Clemente sits between Limon city and Cahuita National
              Park. A working river runs the length of the property to the
              ocean. A beach concession waits at the river mouth.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              <FactCard big="160" label="Hectares" />
              <FactCard big="8" label="Neighborhoods" />
              <FactCard big="55%+" label="Conserved" />
            </div>

            <Link
              to="/land"
              className="inline-flex items-center gap-2 mt-10 text-sand-300 font-medium hover:text-sand-200 transition-colors"
            >
              Explore the masterplan
              <span aria-hidden>&rarr;</span>
            </Link>
          </div>
        </div>
      </section>

      {/* FOUR TIERS: photo-led cards */}
      <section className="py-24 lg:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <SectionEyebrow>The four tiers</SectionEyebrow>
            <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-tight text-forest-900">
              Find the lot that{' '}
              <span className="italic text-clay-500">fits the life.</span>
            </h2>
            <p className="mt-6 text-forest-900/70 text-lg leading-relaxed">
              Four distinct buyer tiers. One coherent village. Phase 1
              inventory is open now.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {lotTiers.map((tier, idx) => (
              <Reveal key={tier.id} delay={(idx * 100) as 0 | 100 | 200 | 300}>
                <Link
                  to="/lots"
                  className="group block bg-cream border border-forest-900/10 hover:border-clay-500 hover:shadow-xl transition-all overflow-hidden card-lift h-full flex flex-col"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <ImageSlot
                      id={`home.tier.${tier.id}`}
                      className="w-full h-full"
                      aspect="auto"
                    />
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <div className="eyebrow text-clay-600">
                      {tier.name.replace('The ', '')}
                    </div>
                    <div className="mt-3 text-sm text-forest-900/60">
                      {tier.size}
                    </div>
                    <div className="mt-6 font-display text-4xl text-forest-900 leading-none">
                      {tier.startingPrice.replace('+', '')}
                    </div>
                    <div className="text-xs uppercase tracking-wider text-forest-900/50">
                      Starting from
                    </div>
                    <p className="mt-5 text-sm leading-relaxed text-forest-900/70 flex-1">
                      {tier.description.split('. ')[0]}.
                    </p>
                    <div className="mt-6 text-sm text-clay-600 group-hover:text-clay-700 inline-flex items-center gap-2">
                      Details
                      <span aria-hidden>&rarr;</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VISION + photo */}
      <section className="py-24 lg:py-32 bg-forest-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-5">
              <SectionEyebrow>The vision</SectionEyebrow>
              <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-tight text-forest-900">
                Regeneration as{' '}
                <span className="italic text-clay-500">strategy.</span>
                <br />
                Not decoration.
              </h2>
              <p className="mt-6 text-forest-900/80 text-lg leading-relaxed">
                The forest does not get cleared for the village. The village
                gets built around the forest. Every cluster, every trail,
                every shared space defers to the river. The river is the
                spine. The forest is the body. The ocean is the reward.
              </p>
              <Link
                to="/vision"
                className="inline-flex items-center gap-2 mt-8 text-clay-600 font-medium hover:text-clay-700 transition-colors"
              >
                Read the manifesto
                <span aria-hidden>&rarr;</span>
              </Link>

              <div className="mt-10 grid grid-cols-2 gap-4">
                <CommitmentCard top="55%+" label="Permanent conservation" />
                <CommitmentCard top="Net-zero" label="Energy target" />
                <CommitmentCard top="100%" label="Biological wastewater" />
                <CommitmentCard top="Native" label="Landscaping only" />
              </div>
            </div>
            <div className="lg:col-span-7 aspect-[4/5] lg:aspect-[5/6] overflow-hidden">
              <ImageSlot
                id="home.vision"
                className="w-full h-full"
                aspect="auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>Built to standard</SectionEyebrow>
            <h2 className="mt-6 font-display text-4xl lg:text-6xl text-forest-900">
              Four certifications.{' '}
              <span className="italic text-clay-500">
                Pursued in parallel.
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-forest-900/10">
            <CertCard
              code="CST"
              title="Sustainable Tourism"
              body="Costa Rica's national tourism standard."
            />
            <CertCard
              code="LEED-ND"
              title="Communities"
              body="Masterplan-scale sustainability. Rare in CR."
            />
            <CertCard
              code="EDGE"
              title="IFC Green Building"
              body="Building-level cert. Path for buyers' homes."
            />
            <CertCard
              code="Blue Flag"
              title="Bandera Azul"
              body="National environmental performance award."
            />
          </div>
        </div>
      </section>

      {/* CALCULATOR PREVIEW */}
      <section className="py-24 lg:py-32 bg-forest-900 text-cream relative overflow-hidden">
        <div className="absolute inset-0 opacity-15 grain" aria-hidden />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <SectionEyebrow>
                <span className="text-sand-300">The Founder Calculator</span>
              </SectionEyebrow>
              <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-tight">
                What does land in Nativa{' '}
                <span className="italic text-sand-300">actually</span> earn?
              </h2>
              <p className="mt-6 text-cream/80 text-lg leading-relaxed">
                Compare a Phase 1 lot against gold over your hold period.
                Model real Airbnb income. See where prices land as the
                Caribbean closes the gap with the Pacific coast.
              </p>
              <p className="mt-6 font-display italic text-xl text-sand-300">
                For the price of a Garden lot, you could buy 74 ounces of
                gold. In five years, those bars would be worth $510K. But
                you cannot sleep in a gold bar.
              </p>
              <Link
                to="/calculator"
                className="inline-flex items-center gap-2 mt-8 px-7 py-4 bg-sand-300 text-forest-900 font-medium tracking-wide hover:bg-sand-200 transition-colors"
              >
                Run my numbers &rarr;
              </Link>
            </div>

            <div className="lg:col-span-6">
              <div className="border border-cream/15 bg-forest-950/60 p-8 lg:p-10 backdrop-blur-sm">
                <div className="space-y-6">
                  <Row label="Lot tier" value="Garden . $350K" />
                  <Row label="Hold period" value="5 years" />
                  <div className="divider-rule" />
                  <Row
                    label="Lot value at year 5"
                    value="$1.05M"
                    emphasis
                  />
                  <Row
                    label="Cumulative rental"
                    value="$215K"
                    emphasis
                  />
                  <div className="divider-rule" />
                  <Row
                    label="Total return"
                    value="+$915K"
                    emphasis
                    highlight
                  />
                </div>
                <p className="mt-8 text-xs uppercase tracking-widest-2 text-cream/50">
                  Moderate scenario . Part-time Airbnb . Sample only, your
                  numbers will vary
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING CTA over photo */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <ImageSlot
            id="home.closing"
            className="absolute inset-0 w-full h-full"
            aspect="auto"
          />
        </div>
        <div className="absolute inset-0 bg-forest-900/60" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="w-20 h-20 mx-auto mb-8">
            <img
              src="/favicon2.svg"
              alt="Costa Nativa"
              className="w-full h-full object-contain"
            />
          </div>
          <SectionEyebrow className="justify-center text-sand-300">
            Phase 1 inventory is limited
          </SectionEyebrow>
          <h2 className="mt-6 font-display text-5xl lg:text-7xl leading-[1.05] text-cream">
            Reserve your lot before the{' '}
            <span className="italic text-sand-300">next phase</span> prices
            in.
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-lg text-cream/75 leading-relaxed">
            The first 40 to 60 lots are open now. Refundable reservation
            deposit. Visit the land. Walk the river. Choose your spot.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link
              to="/reserve"
              className="px-8 py-4 bg-sand-300 text-forest-900 font-medium tracking-wide hover:bg-cream transition-colors"
            >
              Reserve Now
            </Link>
            <a
              href="mailto:hello@costanativa.co?subject=Founder%20List"
              className="px-8 py-4 border border-cream/40 text-cream font-medium tracking-wide hover:bg-cream hover:text-forest-900 transition-colors"
            >
              Join the Founder List
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ top, label, sub }: { top: string; label: string; sub: string }) {
  return (
    <div>
      <div className="font-display text-4xl lg:text-5xl text-cream">{top}</div>
      <div className="mt-2 text-sm text-cream/80">{label}</div>
      <div className="text-xs text-cream/50 uppercase tracking-wider">
        {sub}
      </div>
    </div>
  );
}

function FactCard({ big, label }: { big: string; label: string }) {
  return (
    <div className="border-t border-cream/20 pt-4">
      <div className="font-display text-3xl lg:text-4xl text-sand-300 leading-none">
        {big}
      </div>
      <div className="mt-2 text-xs uppercase tracking-widest-2 text-cream/80">
        {label}
      </div>
    </div>
  );
}

function CommitmentCard({ top, label }: { top: string; label: string }) {
  return (
    <div className="p-6 bg-cream border border-forest-900/10">
      <div className="font-display text-2xl lg:text-3xl text-forest-900 leading-none">
        {top}
      </div>
      <div className="mt-3 text-xs uppercase tracking-widest-2 text-clay-600">
        {label}
      </div>
    </div>
  );
}

function CertCard({
  code,
  title,
  body,
}: {
  code: string;
  title: string;
  body: string;
}) {
  return (
    <div className="bg-cream p-8">
      <div className="font-mono text-clay-600 font-medium text-sm tracking-wide">
        {code}
      </div>
      <div className="mt-2 font-display text-2xl text-forest-900">{title}</div>
      <p className="mt-3 text-sm text-forest-900/70 leading-relaxed">{body}</p>
    </div>
  );
}

function Row({
  label,
  value,
  emphasis = false,
  highlight = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-cream/70 text-sm uppercase tracking-wider">
        {label}
      </span>
      <span
        className={`font-display ${
          emphasis ? 'text-3xl' : 'text-xl'
        } ${highlight ? 'text-sand-300' : 'text-cream'}`}
      >
        {value}
      </span>
    </div>
  );
}
